/**
 * API access for the web UI. All business rules live in the API; the UI
 * only renders what it returns and submits user actions.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./auth.js";

export type * from "@repu/api-contract";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** Shapes returned by endpoints without a contract schema. */
export interface ArtifactSummary {
  id: string;
  target: string;
  kind: string;
  commitSha: string;
  ref: string;
  pipelineId: string;
  sha256: string;
  sizeBytes: number;
  toolchain: Record<string, string>;
  environment: string | null;
  status: "pending" | "available";
  createdAt: string;
}

export interface SecretMetadata {
  name: string;
  environment: string;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

export interface AuditEntry {
  id: string;
  actor: { type: string; userId?: string; event?: string };
  action: string;
  resource: { type: string; id: string };
  outcome: "success" | "denied" | "failure";
  at: string;
}

export interface GitHubStatus {
  configured: boolean;
  installation: { id: number; account: string; type: string; deleted: boolean } | null;
  repositories: { id: string; fullName: string; defaultBranch: string; visibility: string }[];
}

export type Fetcher = <T>(path: string, init?: { method?: string; body?: unknown }) => Promise<T>;

export async function apiFetch<T>(token: string | null, path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init.method ?? "GET",
    headers: {
      accept: "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiError(res.status, "invalid_response", `Unexpected response (${res.status})`);
  }
  if (!res.ok) {
    const p = (json ?? {}) as { code?: string; detail?: string; title?: string; errors?: unknown };
    throw new ApiError(res.status, p.code ?? `http.${res.status}`, p.detail ?? p.title ?? `HTTP ${res.status}`, p.errors);
  }
  return json as T;
}

/** Authenticated fetcher bound to the signed-in user. */
export function useFetcher(): Fetcher {
  const auth = useAuth();
  return useCallback(async <T,>(path: string, init?: { method?: string; body?: unknown }) => apiFetch<T>(await auth.token(), path, init), [auth]);
}

export interface Query<T> {
  data: T | undefined;
  error: ApiError | Error | undefined;
  loading: boolean;
  reload(): void;
}

/**
 * Loads `path` (null to skip) and optionally refreshes it while `refreshMs`
 * is set — used for live pipeline and deployment status.
 */
export function useApi<T>(path: string | null, options: { refreshMs?: number } = {}): Query<T> {
  const fetcher = useFetcher();
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError | Error>();
  const [loading, setLoading] = useState(path !== null);
  const [version, setVersion] = useState(0);
  const current = useRef(path);
  current.current = path;

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    setLoading(true);
    fetcher<T>(path).then(
      (d) => {
        if (cancelled || current.current !== path) return;
        setData(d);
        setError(undefined);
        setLoading(false);
      },
      (e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [path, fetcher, version]);

  useEffect(() => {
    if (!options.refreshMs || path === null) return;
    const timer = setInterval(() => {
      setVersion((v) => v + 1);
    }, options.refreshMs);
    return () => {
      clearInterval(timer);
    };
  }, [options.refreshMs, path]);

  return {
    data,
    error,
    loading: loading && data === undefined,
    reload: () => {
      setVersion((v) => v + 1);
    },
  };
}

/** Streams job logs (Server-Sent Events over fetch, so the Authorization header can be sent). */
export async function streamLogs(token: string, org: string, jobId: string, onText: (text: string) => void, signal: AbortSignal): Promise<string | undefined> {
  let after = -1;
  let endStatus: string | undefined;
  while (!signal.aborted && endStatus === undefined) {
    const res = await fetch(`/api/v1/orgs/${org}/jobs/${jobId}/logs?follow=true&after=${after}`, { headers: { authorization: `Bearer ${token}` }, signal });
    if (!res.ok || !res.body) throw new ApiError(res.status, "logs.unavailable", `Logs unavailable (${res.status})`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let index;
      while ((index = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, index);
        buffer = buffer.slice(index + 2);
        const event = /^event: (.*)$/m.exec(block)?.[1];
        const id = /^id: (.*)$/m.exec(block)?.[1];
        const data = /^data: (.*)$/m.exec(block)?.[1];
        if (event === "log" && data) {
          onText((JSON.parse(data) as { text: string }).text);
          if (id) after = Number(id);
        } else if (event === "end" && data) {
          endStatus = (JSON.parse(data) as { status: string }).status;
        }
      }
    }
  }
  return endStatus;
}
